import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
  Switch,
} from '@mui/material';
import { toast } from 'react-toastify';
import {
  AnnouncementDetail,
  AnnouncementItem,
  ApprovalForwardSettingItem,
  EmployeeOption,
  OrganizationOption,
  toolsApi,
} from '../api/toolsApi';

type TabKey = ' forward | announcements';
const ALL_ROWS_PAGE_SIZE = 100000;
const ROWS_PER_PAGE_OPTIONS = [10, 50, 100, { label: 'All', value: -1 }];

interface ToolsPageProps {
  defaultTab?:  'forward-settings'|'announcements';
}

const ToolsPage: React.FC<ToolsPageProps> = ({ defaultTab = 'forward-settings' }) => {
  //const [activeTab, setActiveTab] = useState<TabKey>(defaultTab === 'announcements' ? 'forward' : 'announcements');

  const [organizations, setOrganizations] = useState<OrganizationOption[]>([]);
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [lookupLoading, setLookupLoading] = useState(false);

  const [announcements, setAnnouncements] = useState<AnnouncementItem[]>([]);
  const [announcementSearch, setAnnouncementSearch] = useState('');
  const [announcementPage, setAnnouncementPage] = useState(0);
  const [announcementRowsPerPage, setAnnouncementRowsPerPage] = useState(10);
  const [announcementTotal, setAnnouncementTotal] = useState(0);
  const [announcementLoading, setAnnouncementLoading] = useState(false);

  const [composeOpen, setComposeOpen] = useState(false);
  const [sendingAnnouncement, setSendingAnnouncement] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [selectedOrganizationIds, setSelectedOrganizationIds] = useState<string[]>([]);
  const [attachment, setAttachment] = useState<File | undefined>();

  const [viewingAnnouncement, setViewingAnnouncement] = useState<AnnouncementDetail | null>(null);
  const [viewLoading, setViewLoading] = useState(false);

  const [forwardItems, setForwardItems] = useState<ApprovalForwardSettingItem[]>([]);
  const [forwardLoading, setForwardLoading] = useState(false);
  const [forwardSearch, setForwardSearch] = useState('');
  const [forwardFromFilter, setForwardFromFilter] = useState('');
  const [forwardToFilter, setForwardToFilter] = useState('');
  const [forwardPage, setForwardPage] = useState(0);
  const [forwardRowsPerPage, setForwardRowsPerPage] = useState(10);
  const [forwardTotal, setForwardTotal] = useState(0);

  const [forwardDialogOpen, setForwardDialogOpen] = useState(false);
  const [savingForward, setSavingForward] = useState(false);
  const [editingForwardId, setEditingForwardId] = useState<string | null>(null);
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [effectiveTo, setEffectiveTo] = useState('');
  const [fromEmployeeId, setFromEmployeeId] = useState('');
  const [toEmployeeId, setToEmployeeId] = useState('');
  const [forwardIsActive, setForwardIsActive] = useState(true);

  const organizationMap = useMemo(
    () => new Map(organizations.map((x) => [x.id, x.name])),
    [organizations]
  );

  const employeeMap = useMemo(
    () => new Map(employees.map((employee) => [employee.id, employee])),
    [employees]
  );

  const formatEmployeeLabel = (employeeId: string, fallbackName?: string) => {
    const employee = employeeMap.get(employeeId);
    if (!employee) return fallbackName || employeeId;
    return employee.employeeCode ? `${employee.name} (${employee.employeeCode})` : employee.name;
  };

  const forwardSummary = useMemo(() => {
    const activeCount = forwardItems.filter((item) => item.status === 'Active').length;
    const inactiveCount = forwardItems.length - activeCount;
    return {
      total: forwardTotal || forwardItems.length,
      activeCount,
      inactiveCount,
    };
  }, [forwardItems, forwardTotal]);

  const loadLookups = async () => {
    setLookupLoading(true);
    try {
      const [orgs, emps] = await Promise.all([toolsApi.getOrganizations(), toolsApi.getEmployees()]);
      setOrganizations(orgs);
      setEmployees(emps);
    } catch {
      toast.error('Failed to load Tools dropdown data');
    } finally {
      setLookupLoading(false);
    }
  };

  const loadAnnouncements = async () => {
    setAnnouncementLoading(true);
    try {
      const result = await toolsApi.getAnnouncements(
        announcementPage + 1,
        announcementRowsPerPage === -1 ? ALL_ROWS_PAGE_SIZE : announcementRowsPerPage,
        announcementSearch.trim() || undefined
      );
      setAnnouncements(result.data);
      setAnnouncementTotal(result.total);
    } catch {
      toast.error('Failed to load announcements');
    } finally {
      setAnnouncementLoading(false);
    }
  };

  const loadForwardSettings = async () => {
    setForwardLoading(true);
    try {
      const result = await toolsApi.getApprovalForwardSettings({
        fromEmployeeId: forwardFromFilter || undefined,
        toEmployeeId: forwardToFilter || undefined,
        searchTerm: forwardSearch.trim() || undefined,
        pageNumber: forwardPage + 1,
        pageSize: forwardRowsPerPage === -1 ? ALL_ROWS_PAGE_SIZE : forwardRowsPerPage,
      });
      setForwardItems(result.data);
      setForwardTotal(result.total);
    } catch {
      toast.error('Failed to load approval forward settings');
    } finally {
      setForwardLoading(false);
    }
  };

  useEffect(() => {
    void loadLookups();
  }, []);

  // useEffect(() => {
  //   if (activeTab === 'forward') {
  //     void loadAnnouncements();
  //   }
  // }, [activeTab, announcementPage, announcementRowsPerPage]);

  useEffect(() => {
    
      void loadForwardSettings();
    
  }, [forwardPage, forwardRowsPerPage]);

  const resetAnnouncementForm = () => {
    setSubject('');
    setBody('');
    setSelectedOrganizationIds([]);
    setAttachment(undefined);
  };

  const handleSendAnnouncement = async () => {
    if (!subject.trim() || !body.trim() || selectedOrganizationIds.length === 0) {
      toast.warning('Subject, body and organization are required');
      return;
    }

    setSendingAnnouncement(true);
    try {
      const result = await toolsApi.sendAnnouncement({
        subject: subject.trim(),
        body: body.trim(),
        organizationIds: selectedOrganizationIds,
        document: attachment,
      });
      const parts: string[] = [];
      if (result.emailsSent > 0) parts.push(`${result.emailsSent} email${result.emailsSent !== 1 ? 's' : ''}`);
      if (result.whatsAppsSent > 0) parts.push(`${result.whatsAppsSent} WhatsApp${result.whatsAppsSent !== 1 ? 's' : ''}`);
      const detail = parts.length > 0 ? ` — ${parts.join(', ')} sent` : '';
      toast.success(`Announcement sent to ${result.totalRecipients} recipient${result.totalRecipients !== 1 ? 's' : ''}${detail}`);
      setComposeOpen(false);
      resetAnnouncementForm();
      setAnnouncementPage(0);
      await loadAnnouncements();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to send announcement');
    } finally {
      setSendingAnnouncement(false);
    }
  };

  const handleViewAnnouncement = async (id: string) => {
    setViewLoading(true);
    try {
      const data = await toolsApi.getAnnouncementById(id);
      setViewingAnnouncement(data);
    } catch {
      toast.error('Failed to load announcement details');
    } finally {
      setViewLoading(false);
    }
  };

  const resetForwardForm = () => {
    setEditingForwardId(null);
    setEffectiveFrom('');
    setEffectiveTo('');
    setFromEmployeeId('');
    setToEmployeeId('');
    setForwardIsActive(true);
  };

  const openAddForwardDialog = () => {
    resetForwardForm();
    setForwardDialogOpen(true);
  };

  const openEditForwardDialog = (item: ApprovalForwardSettingItem) => {
    setEditingForwardId(item.id);
    setEffectiveFrom(item.effectiveFrom.slice(0, 10));
    setEffectiveTo(item.effectiveTo.slice(0, 10));
    setFromEmployeeId(item.fromEmployeeId);
    setToEmployeeId(item.toEmployeeId);
    setForwardIsActive(item.isActive);
    setForwardDialogOpen(true);
  };

  const handleSaveForwardSetting = async () => {
    if (!effectiveFrom || !effectiveTo || !fromEmployeeId || !toEmployeeId) {
      toast.warning('All fields are required');
      return;
    }

    if (effectiveFrom > effectiveTo) {
      toast.warning('Effective From must be before or equal to Effective To');
      return;
    }

    if (fromEmployeeId === toEmployeeId) {
      toast.warning('From Employee and To Employee cannot be the same');
      return;
    }

    setSavingForward(true);
    try {
      const payload = {
        effectiveFrom,
        effectiveTo,
        fromEmployeeId,
        toEmployeeId,
        isActive: forwardIsActive,
      };

      if (editingForwardId) {
        await toolsApi.updateApprovalForwardSetting(editingForwardId, payload);
        toast.success('Forward setup updated');
      } else {
        await toolsApi.createApprovalForwardSetting(payload);
        toast.success('Forward setup created');
      }

      setForwardDialogOpen(false);
      resetForwardForm();
      setForwardPage(0);
      await loadForwardSettings();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to save forward setup');
    } finally {
      setSavingForward(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={2}>
        Tools
      </Typography>

      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {/* <Tabs
          value={activeTab}
          onChange={(_, value: TabKey) => setActiveTab(value)}
          variant="fullWidth"
        > */}
          {/* <Tab value="announcements" label="Announcements" sx={{ textTransform: 'none' }} />
          <Tab value="forward" label="Approval Settings" sx={{ textTransform: 'none' }} /> */}
        {/* </Tabs> */}
      </Paper>

      {/* {activeTab === 'announcements' && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} justifyContent="space-between" mb={2}>
            <TextField
              size="small"
              placeholder="Search announcements"
              value={announcementSearch}
              onChange={(e) => setAnnouncementSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setAnnouncementPage(0);
                  void loadAnnouncements();
                }
              }}
              sx={{ minWidth: { xs: '100%', sm: 300 } }}
            />
            <Stack direction="row" gap={1}>
              <Button variant="outlined" onClick={() => { setAnnouncementPage(0); void loadAnnouncements(); }}>
                Search
              </Button>
              <Button variant="contained" onClick={() => setComposeOpen(true)} disabled={lookupLoading}>
                Send an Announcement
              </Button>
            </Stack>
          </Stack>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Subject</strong></TableCell>
                  <TableCell><strong>Sent By</strong></TableCell>
                  <TableCell><strong>Sent At</strong></TableCell>
                  <TableCell align="center"><strong>Action</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {announcementLoading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : announcements.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                      No announcements found
                    </TableCell>
                  </TableRow>
                ) : (
                  announcements.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{item.subject}</TableCell>
                      <TableCell>{item.sentBy}</TableCell>
                      <TableCell>{new Date(item.sentAt).toLocaleString()}</TableCell>
                      <TableCell align="center">
                        <Button size="small" onClick={() => void handleViewAnnouncement(item.id)}>View</Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={announcementTotal}
            page={announcementPage}
            onPageChange={(_, p) => setAnnouncementPage(p)}
            rowsPerPage={announcementRowsPerPage}
            onRowsPerPageChange={(e) => {
              setAnnouncementRowsPerPage(Number(e.target.value));
              setAnnouncementPage(0);
            }}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          />
        </Paper>
      )} */}

      {/* {activeTab === 'forward' && ( */}
        <Paper sx={{ p: 2, mt: 2 }}>
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="h6" fontWeight={700}>Approval Forward Settings</Typography>
              <Typography variant="body2" color="text.secondary">
                Temporarily delegate approvals from one employee to another for a defined date range.
              </Typography>
            </Box>

            <Alert severity="info">
              Use this when an approver is on leave, unavailable, or needs a backup approver for a specific period.
            </Alert>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
                gap: 1.5,
              }}
            >
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="body2" color="text.secondary">Total Delegations</Typography>
                  <Typography variant="h5" fontWeight={700}>{forwardSummary.total}</Typography>
                </CardContent>
              </Card>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="body2" color="text.secondary">Active</Typography>
                  <Typography variant="h5" fontWeight={700} color="success.main">{forwardSummary.activeCount}</Typography>
                </CardContent>
              </Card>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="body2" color="text.secondary">Inactive</Typography>
                  <Typography variant="h5" fontWeight={700} color="text.secondary">{forwardSummary.inactiveCount}</Typography>
                </CardContent>
              </Card>
            </Box>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={1.5}>
                <Typography variant="subtitle1" fontWeight={600}>Filters</Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr)) auto' },
                    gap: 1.5,
                    alignItems: 'center',
                  }}
                >
                  <FormControl size="small" fullWidth>
                    <InputLabel>From Employee</InputLabel>
                    <Select
                      value={forwardFromFilter}
                      label="From Employee"
                      onChange={(e) => setForwardFromFilter(e.target.value)}
                    >
                      <MenuItem value=""><em>All</em></MenuItem>
                      {employees.map((emp) => (
                        <MenuItem key={emp.id} value={emp.id}>{formatEmployeeLabel(emp.id)}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Delegate To</InputLabel>
                    <Select
                      value={forwardToFilter}
                      label="Delegate To"
                      onChange={(e) => setForwardToFilter(e.target.value)}
                    >
                      <MenuItem value=""><em>All</em></MenuItem>
                      {employees.map((emp) => (
                        <MenuItem key={emp.id} value={emp.id}>{formatEmployeeLabel(emp.id)}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    fullWidth
                    label="Search by name"
                    placeholder="Search employee"
                    value={forwardSearch}
                    onChange={(e) => setForwardSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        setForwardPage(0);
                        void loadForwardSettings();
                      }
                    }}
                  />
                  <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                    <Button variant="outlined" onClick={() => { setForwardPage(0); void loadForwardSettings(); }} sx={{textTransform:'none'}}>
                      Apply
                    </Button>
                    <Button
                      variant="text"
                      onClick={() => {
                        setForwardFromFilter('');
                        setForwardToFilter('');
                        setForwardSearch('');
                        setForwardPage(0);
                        void loadForwardSettings();
                      }}
                      sx={{textTransform:'none'}}
                    >
                      Reset
                    </Button>
                  </Stack>
                </Box>
              </Stack>
            </Paper>

            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1.5}>
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>Delegation List</Typography>
                <Typography variant="body2" color="text.secondary">
                  Review and update active approval delegation windows.
                </Typography>
              </Box>
              <Button variant="contained" onClick={openAddForwardDialog} disabled={lookupLoading} sx={{ textTransform: 'none' }}>
                Add Delegation
              </Button>
            </Stack>
          </Stack>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Approval Owner</strong></TableCell>
                  <TableCell><strong>Delegated To</strong></TableCell>
                  <TableCell><strong>Effective Window</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {forwardLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : forwardItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      No approval delegation found
                    </TableCell>
                  </TableRow>
                ) : (
                  forwardItems.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{formatEmployeeLabel(item.fromEmployeeId, item.fromEmployeeName)}</TableCell>
                      <TableCell>{formatEmployeeLabel(item.toEmployeeId, item.toEmployeeName)}</TableCell>
                      <TableCell>
                        <Stack spacing={0.25}>
                          <Typography variant="body2">{new Date(item.effectiveFrom).toLocaleDateString()}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            to {new Date(item.effectiveTo).toLocaleDateString()}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={item.status === 'Active' ? 'success' : 'default'}
                          label={item.status}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Button size="small" onClick={() => openEditForwardDialog(item)}>Edit</Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={forwardTotal}
            page={forwardPage}
            onPageChange={(_, p) => setForwardPage(p)}
            rowsPerPage={forwardRowsPerPage}
            onRowsPerPageChange={(e) => {
              setForwardRowsPerPage(Number(e.target.value));
              setForwardPage(0);
            }}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          />
        </Paper>
      {/* )} */}

      <Dialog open={composeOpen} onClose={() => !sendingAnnouncement && setComposeOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Send an Announcement</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              size="small"
              label="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              fullWidth
            />
            <TextField
              size="small"
              label="Body"
              multiline
              minRows={5}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              fullWidth
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Select Organization</InputLabel>
              <Select
                multiple
                value={selectedOrganizationIds}
                label="Select Organization"
                onChange={(e) => setSelectedOrganizationIds(e.target.value as string[])}
                renderValue={(selected) =>
                  (selected as string[])
                    .map((id) => organizationMap.get(id) || id)
                    .join(', ')
                }
              >
                {organizations.map((org) => (
                  <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="outlined" component="label">
              {attachment ? attachment.name : 'Document Upload'}
              <input
                hidden
                type="file"
                accept=".pdf,.docx,.doc,.jpeg,.jpg,.png"
                onChange={(e) => setAttachment(e.target.files?.[0])}
              />
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setComposeOpen(false)} disabled={sendingAnnouncement} sx={{ textTransform: 'none' }}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleSendAnnouncement()}
            disabled={sendingAnnouncement || !subject.trim() || !body.trim() || selectedOrganizationIds.length === 0}
            sx={{ textTransform: 'none' }}
          >
            {sendingAnnouncement ? 'Sending...' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!viewingAnnouncement || viewLoading} onClose={() => setViewingAnnouncement(null)} maxWidth="md" fullWidth>
        <DialogTitle>Announcement Details</DialogTitle>
        <DialogContent>
          {viewLoading || !viewingAnnouncement ? (
            <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <Typography variant="subtitle1" fontWeight={700}>{viewingAnnouncement.subject}</Typography>
              <Typography variant="body2" color="text.secondary">
                Sent by {viewingAnnouncement.sentBy} on {new Date(viewingAnnouncement.sentAt).toLocaleString()}
              </Typography>
              <Typography variant="body1" whiteSpace="pre-wrap">{viewingAnnouncement.body}</Typography>
              <Typography variant="body2">
                Organizations: {viewingAnnouncement.organizationIds.map((id) => organizationMap.get(id) || id).join(', ') || '-'}
              </Typography>
              {viewingAnnouncement.attachmentFilePath && (
                <Typography variant="body2">
                  Attachment:{' '}
                  <Link href={`/${viewingAnnouncement.attachmentFilePath}`} target="_blank" rel="noopener">
                    {viewingAnnouncement.attachmentFileName || 'Download'}
                  </Link>
                </Typography>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewingAnnouncement(null)} sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={forwardDialogOpen} onClose={() => !savingForward && setForwardDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingForwardId ? 'Edit Approval Delegation' : 'Add Approval Delegation'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              The selected delegate will receive approval responsibility for the selected employee during this date range.
            </Alert>
            <TextField
              size="small"
              type="date"
              label="Effective From"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              size="small"
              type="date"
              label="Effective To"
              value={effectiveTo}
              onChange={(e) => setEffectiveTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: effectiveFrom || undefined }}
              fullWidth
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Approval Owner</InputLabel>
              <Select value={fromEmployeeId} label="Approval Owner" onChange={(e) => setFromEmployeeId(e.target.value)}>
                {employees.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>{formatEmployeeLabel(emp.id)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Delegate To</InputLabel>
              <Select value={toEmployeeId} label="Delegate To" onChange={(e) => setToEmployeeId(e.target.value)}>
                {employees.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>{formatEmployeeLabel(emp.id)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={forwardIsActive}
                  onChange={(e) => setForwardIsActive(e.target.checked)}
                />
              }
              label={forwardIsActive ? 'Status: Active' : 'Status: Inactive'}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForwardDialogOpen(false)} disabled={savingForward} sx={{ textTransform: 'none' }}>
            Close
          </Button>
          <Button variant="contained" onClick={() => void handleSaveForwardSetting()} disabled={savingForward} sx={{ textTransform: 'none' }}>
            {savingForward ? 'Saving...' : editingForwardId ? 'Update Delegation' : 'Create Delegation'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ToolsPage;
